import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import {
  loadUserSettings,
  loadUserSettingsSuccess,
  loadUserSettingsFailure,
} from '@frontend/data-access-user-settings';
import { filter, firstValueFrom, take, timeout } from 'rxjs';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { Router, TitleStrategy } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { appRoutes } from './app.routes';
import { AppTitleStrategy } from './title-strategy';
import {
  AuthInterceptor,
  loginFactory,
  LoginService,
  LoginServiceJwt,
  LoginServiceSchueco,
} from '@frontend/util-client-auth';
import { KeycloakBearerInterceptor, KeycloakEventTypeLegacy, KeycloakService } from 'keycloak-angular';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { API_DATA, CLIENT_DATA, ClientData, DEPLOY_PREFIX, TRUSTCOMMANDER_CONFIG } from '@frontend/util-api-data';
import { environment } from '../environments/environment';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { translateLoaderProviders } from '@frontend/localization';
import { BASIC_AUTH_INTERCEPTOR_CONFIG, BasicAuthInterceptor } from '@frontend/util-interceptors';
import { NavigationActionTiming, provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideTracking, TRACKING_DATA } from '@schueco/angular-tracking';
import { Location } from '@angular/common';
import * as categoryTree from '@frontend/data-access-category-tree';
import * as fromCategories from '@frontend/data-access-categories';
import * as fromDocuments from '@frontend/data-access-documents';
import * as fromDocumentDetails from '@frontend/data-access-document-details';
import * as fromDocumentDetailsMeta from '@frontend/data-access-document-details-meta';
import * as fromPage from '@frontend/data-access-page';
import * as general from '@frontend/data-access-general';
import * as downloadCollection from '@frontend/data-access-download-collection';
import * as downloadDocument from '@frontend/data-access-download-document';
import * as downloadList from '@frontend/data-access-download-list';
import * as fromSearchDocuments from '@frontend/data-access-search';
import { AuthErrorInterceptor } from '@frontend/feature-login';
import * as landRole from '@frontend/data-access-land-role';
import { WebtrekkSmartPixelAngular } from '@webtrekk-smart-pixel/angular';
import { REDIRECT_CONFIG } from '@frontend/util-redirects';
import * as userInfo from '@frontend/data-access-user-info';
import { MaintenanceInterceptor, MaintenanceService } from '@frontend/feature-maintenance';
import * as featureFlags from '@frontend/feature-flags';
import { FeatureFlagsService, TogglesFacade, UNLEASH_CONFIGURATION } from '@frontend/feature-flags';
import { DownloadListSyncService } from '@frontend/data-access-download-list';
import * as userSettings from '@frontend/data-access-user-settings';
import { LAND_ROLE_VIEW_KEY } from '@frontend/data-access-land-role';
import * as bookmarks from '@frontend/data-access-bookmarks';
import { getRedirectUrl, RedirectConfigInterface } from '@frontend/util-redirects';
import * as changelog from '@frontend/data-access-changelog';
import * as weblinks from '@frontend/data-access-weblinks';
import * as categoryFavorites from '@frontend/data-access-category-favorite';
import * as news from '@frontend/data-access-news';

// After a new deployment, previously loaded chunk hashes no longer exist on the server,
// so an already-open tab fails to lazy-load routes/chunks. Reload once to fetch the latest build.
// Message wording differs per browser: Chrome/Edge, Firefox and Safari each phrase it differently.
const CHUNK_LOAD_ERROR_REGEX =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/;
const CHUNK_RELOAD_FLAG = 'chunk-load-error-reloaded';

class ChunkLoadAwareErrorHandler implements ErrorHandler {
  private readonly sentryHandler = Sentry.createErrorHandler();

  handleError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);

    if (CHUNK_LOAD_ERROR_REGEX.test(message) && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      window.location.reload();
      return;
    }

    this.sentryHandler.handleError(error);
  }
}

// Kombinierter Initializer: Legacy-Redirect-Check, Keycloak, UserSettings, UI-Sprache
// WICHTIG: Alles in EINEM Initializer, um Race Conditions zu vermeiden
export function initializeApp(
  httpClient: HttpClient,
  keycloak: KeycloakService,
  clientData: ClientData,
  store: Store,
  actions$: Actions,
  location: Location,
  translateService: TranslateService,
  loginService: LoginService,
) {
  return async () => {
    // 1: Legacy URL Redirect Check
    const redirectConfig: RedirectConfigInterface = {
      api: {
        url: environment.apiData.apiURL,
      },
      targetHost: environment.redirect.targetHost,
    };

    const redirectUrl = await getRedirectUrl(httpClient, redirectConfig);

    if (redirectUrl) {
      window.location.href = redirectUrl;
      // Prevent Angular from continuing initialization
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return new Promise(() => {}); // Never resolves, stops app initialization
    }

    // 2: Client-spezifische CSS-Klasse zum Spinner hinzufügen
    const spinnerElement = document.querySelector('.app-loading-spinner');
    if (spinnerElement) {
      spinnerElement.classList.add(`client-${environment.client.name}`);
    }

    // 3: Sprache aus URL ermitteln
    const browserLang = navigator.language.split('-')[0];
    const currentLangPathSegment =
      location.path().split('/')[1] || getBrowserOrDefaultLanguage(environment.client.supportedLanguages, browserLang);

    // 4: Übersetzungen laden
    try {
      await firstValueFrom(translateService.use(currentLangPathSegment));
    } catch (error) {
      console.warn(`Language '${currentLangPathSegment}' not found, falling back to default`);
      const defaultLang = environment.client.supportedLanguages[0];
      await firstValueFrom(translateService.use(defaultLang));
    }

    // 5: Keycloak initialisieren
    let loggedIn = false;

    if (clientData.name === 'schueco') {
      await keycloak.init({
        config: {
          url: environment.keycloakConfig.url,
          realm: environment.keycloakConfig.realm,
          clientId: environment.keycloakConfig.clientId,
        },
        initOptions: {
          checkLoginIframe: false,
          pkceMethod: 'S256',
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        },
      });
      loggedIn = keycloak.isLoggedIn();
    } else if (clientData.name === 'jansen' || clientData.name === 'aks') {
      loggedIn = await loginService.isLoggedIn();
    }

    // 6: Loading-Indikator aktualisieren
    const loadingElement = document.querySelector('.app-loading-text') as HTMLElement;
    if (loadingElement) {
      const translation = await firstValueFrom(translateService.get('APP_LOADING.TEXT'));
      loadingElement.textContent = translation;
      loadingElement.style.display = 'block';
    }

    const loadingElementSecondary = document.querySelector('.app-loading-text-secondary') as HTMLElement;
    if (loadingElementSecondary) {
      const translation = await firstValueFrom(translateService.get('APP_LOADING.SECONDARY_TEXT'));
      loadingElementSecondary.textContent = translation;
      loadingElementSecondary.style.display = 'block';
    }
    if (loggedIn) {
      store.dispatch(loadUserSettings());
      const result = await firstValueFrom(actions$.pipe(ofType(loadUserSettingsSuccess, loadUserSettingsFailure)));
      //User settings in localStorage speichern
      if (result.type === loadUserSettingsFailure.type) {
        localStorage.setItem(LAND_ROLE_VIEW_KEY, JSON.stringify({ countryId: null, roleId: null }));
        localStorage.setItem('userUiLanguage', '');
      } else if (result.type === loadUserSettingsSuccess.type) {
        const settings = result.userSettings.settings;
        const isValidSettings = settings && !Array.isArray(settings);
        const countryId = isValidSettings && settings.country ? settings.country : null;
        const roleId = isValidSettings && settings.role ? settings.role : null;
        if (countryId !== null && roleId !== null) {
          localStorage.setItem(LAND_ROLE_VIEW_KEY, JSON.stringify({ countryId, roleId }));
        } else {
          localStorage.setItem(LAND_ROLE_VIEW_KEY, JSON.stringify({ countryId: null, roleId: null }));
        }
        const language = isValidSettings && settings.language ? settings.language : '';
        localStorage.setItem('userUiLanguage', language);

        store.dispatch(categoryFavorites.loadCategoryFavorites({ language: language }));
        const resultCategoryFavorites = await firstValueFrom(
          actions$.pipe(
            ofType(categoryFavorites.loadCategoryFavoritesSuccess, categoryFavorites.loadCategoryFavoritesFailure),
          ),
        );
        if (resultCategoryFavorites.type === categoryFavorites.loadCategoryFavoritesFailure.type) {
          console.error('Failed to load category favorites:', resultCategoryFavorites.error);
        }
      }
    } else {
      // Nicht eingeloggt: Defaults setzen
      localStorage.setItem(LAND_ROLE_VIEW_KEY, JSON.stringify({ countryId: null, roleId: null }));
    }

    // Sprachumschaltung nach UserSettings
    let userUiLanguage = localStorage.getItem('userUiLanguage');

    if (!userUiLanguage) {
      const defaultLang = environment.client.supportedLanguages[0];
      userUiLanguage = defaultLang;
    }

    if (
      loggedIn &&
      userUiLanguage &&
      environment.client.supportedLanguages.includes(userUiLanguage) &&
      userUiLanguage !== currentLangPathSegment
    ) {
      const pathWithoutLang = location.path().split('/').slice(2).join('/') || '';
      const newUrl = '/' + userUiLanguage + (pathWithoutLang ? '/' + pathWithoutLang : '');
      window.location.replace(newUrl);
      return;
    }

    // Initialization complete
    return Promise.resolve();
  };
}

// Funktion zur Ermittlung der Sprache basierend auf der Browser-Sprache
function getBrowserOrDefaultLanguage(languages: string[], browserLang?: string, cachedLang?: string): string {
  if (cachedLang) {
    return cachedLang;
  }
  if (browserLang && languages.includes(browserLang)) {
    return browserLang;
  }
  return environment.client.supportedLanguages[0];
}

function initializeFeatureFlags(featureFlagsService: FeatureFlagsService) {
  return () => {
    featureFlagsService.start();
  };
}

function initializeDownloadListSync(downloadListSyncService: DownloadListSyncService) {
  return () => {
    // Service is initialized through constructor
    // Just ensure it's created
    void downloadListSyncService;
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideEffects(),
    provideRouter(appRoutes),
    provideStore({
      router: routerReducer,
    }),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideRouterStore({ navigationActionTiming: NavigationActionTiming.PostActivation }),

    provideState(categoryTree.CATEGORY_TREE_FEATURE_KEY, categoryTree.categoryTreeReducer),
    provideEffects(categoryTree.CategoryTreeEffects),

    provideState(weblinks.WEBLINKS_FEATURE_KEY, weblinks.weblinksReducer),
    provideEffects(weblinks.WeblinksEffects),

    provideState(fromCategories.CATEGORY_FEATURE_KEY, fromCategories.categoryReducer),
    provideEffects(fromCategories.CategoriesEffects),

    provideState(fromDocuments.DOCUMENT_FEATURE_KEY, fromDocuments.documentsReducer),
    provideEffects(fromDocuments.DocumentsEffects),

    provideState(fromDocumentDetails.DOCUMENT_DETAILS_FEATURE_KEY, fromDocumentDetails.documentDetailsReducer),
    provideEffects(fromDocumentDetails.DocumentDetailsEffects),

    provideState(
      fromDocumentDetailsMeta.DOCUMENT_DETAILS_META_FEATURE_KEY,
      fromDocumentDetailsMeta.documentDetailsMetaReducer,
    ),
    provideEffects(fromDocumentDetailsMeta.DocumentDetailsMetaEffects),

    provideState(fromPage.PAGE_FEATURE_KEY, fromPage.pageReducer),
    provideEffects(fromPage.PageEffects),

    provideState(general.GENERAL_FEATURE_KEY, general.generalReducer),

    provideEffects(downloadCollection.DownloadCollectionEffects),
    provideState(downloadCollection.DOWLOAD_COLLECTION_FEATURE_KEY, downloadCollection.downloadCollectionReducer),

    provideEffects(downloadDocument.DownloadDocumentEffects),
    provideState(downloadDocument.DOWNLOAD_DOCUMENT_FEATURE_KEY, downloadDocument.downloadDocumentReducer),

    provideState(downloadList.DOWNLOAD_LIST_FEATURE_KEY, downloadList.downloadListReducer, {
      metaReducers: [downloadList.hydrationMetaReducer],
    }),
    provideEffects(downloadList.DownloadListEffects),

    provideState(fromSearchDocuments.SEARCH_DOCUMENS_FEATURE_KEY, fromSearchDocuments.searchDocumentsReducer),
    provideEffects(fromSearchDocuments.SearchDocumentsEffects),

    provideState(landRole.LAND_ROLE_FEATURE_KEY, landRole.landRoleReducer),
    provideEffects(landRole.LandRoleEffects),

    provideState(userInfo.USER_INFO_FEATURE_KEY, userInfo.userInfoReducer),
    provideEffects(userInfo.UserInfoEffects),

    provideState(featureFlags.TOGGLES_FEATURE_KEY, featureFlags.togglesReducer),
    provideEffects(featureFlags.TogglesEffects),

    provideState(userSettings.USER_SETTINGS_FEATURE_KEY, userSettings.userSettingsReducer),
    provideEffects(userSettings.UserSettingsEffects),

    provideState(bookmarks.BOOKMARKS_FEATURE_KEY, bookmarks.bookmarksReducer),
    provideEffects(bookmarks.BookmarksEffects),

    provideState(changelog.CHANGELOG_FEATURE_KEY, changelog.changelogReducer),
    provideEffects(changelog.ChangelogEffects),

    provideState(categoryFavorites.CATEGORY_FAVORITES_FEATURE_KEY, categoryFavorites.categoryFavoritesReducer),
    provideEffects(categoryFavorites.CategoryFavoriteEffects),

    provideState(news.NEWS_FEATURE_KEY, news.newsReducer),
    provideEffects(news.NewsEffects),

    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: LoginService,
      useFactory: loginFactory,
      deps: [LoginServiceSchueco, LoginServiceJwt],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
      deps: [HttpClient, KeycloakService, CLIENT_DATA, Store, Actions, Location, TranslateService, LoginService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFeatureFlags,
      multi: true,
      deps: [FeatureFlagsService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeDownloadListSync,
      multi: true,
      deps: [DownloadListSyncService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BasicAuthInterceptor,
      multi: true,
    },
    {
      provide: BASIC_AUTH_INTERCEPTOR_CONFIG,
      useValue: {
        basic_auth: environment.basicAuth,
        urlPattern: '(assets/.*|media/.*|mapping_records\\.csv|static_redirects\\.csv)',
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true,
    },
    {
      provide: API_DATA,
      useValue: environment.apiData,
    },
    {
      provide: CLIENT_DATA,
      useValue: environment.client,
    },
    {
      provide: TRACKING_DATA,
      useValue: environment.mapp,
    },
    {
      provide: TRUSTCOMMANDER_CONFIG,
      useValue: environment.trustcommander,
    },
    // only needed for filter-minimal Deploy to Bipod's
    {
      provide: DEPLOY_PREFIX,
      useValue: '',
    },
    KeycloakService,
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideTranslateService(),
    ...translateLoaderProviders,
    {
      provide: WebtrekkSmartPixelAngular,
      useClass: WebtrekkSmartPixelAngular,
    },
    // Tracking configuration with automatic cookie consent integration
    provideTracking({
      config: {
        trackDomain: environment.mapp?.trackDomain || '',
        trackId: environment.mapp?.trackId || '',
      },
      initialize: {
        on: 'cookie-consent',
        vendor: '3', // Vendor "3" is Webtrekk/Mapp
      },
      tokenFactory: async () => {
        // All inject() calls must be synchronous at the top — before any await.
        const clientData = inject(CLIENT_DATA);
        const keycloak = inject(KeycloakService, { optional: true });
        // TODO: #SDC-1240 Tracking for AKS still disabled. Delete console.logs after implementation
        const loginService = inject(LoginService, { optional: true });
        const store = inject(Store, { optional: true });
        const actions$ = inject(Actions);

        try {
          // Schueco uses Keycloak
          if (clientData.name === 'schueco' && keycloak) {
            // tokenFactory may be called before APP_INITIALIZER (Keycloak) finishes
            // because Angular runs all APP_INITIALIZERs concurrently.
            // getKeycloakInstance() returns undefined until keycloak.init() is called,
            // so we must wait for OnReady/OnAuthError before accessing the instance.
            const keycloakInstance = keycloak.getKeycloakInstance();
            if (!keycloakInstance || keycloakInstance.authenticated === undefined) {
              await firstValueFrom(
                keycloak.keycloakEvents$.pipe(
                  filter(
                    (e) => e.type === KeycloakEventTypeLegacy.OnReady || e.type === KeycloakEventTypeLegacy.OnAuthError,
                  ),
                  take(1),
                ),
              ).catch(() => null);
            }

            const isLoggedIn = keycloak.isLoggedIn();
            //console.log('[Tracking] Keycloak ready, isLoggedIn:', isLoggedIn);

            if (isLoggedIn) {
              const tokenParsed = keycloak.getKeycloakInstance()?.tokenParsed;
              if (tokenParsed) {
                //console.log('[Tracking] Extracted tokenParsed for tracking:', tokenParsed);
                return tokenParsed;
              }
            }
          }

          // Jansen uses JWT-based authentication
          // We need to get user data from the store (UserInfo API)
          // because JWT token doesn't contain tracking fields (country, business_partner_type)
          else if (clientData.name === 'jansen' && loginService && store) {
            const isLoggedIn = await loginService.isLoggedIn();
            //console.log('[Tracking] JWT LoginService, isLoggedIn:', isLoggedIn);

            if (isLoggedIn) {
              let userInfoState = await firstValueFrom(store.select(userInfo.selectUserInfo));

              if (!userInfoState) {
                userInfoState = await firstValueFrom(
                  actions$.pipe(
                    ofType(userInfo.loadUserInfoSuccess, userInfo.loadUserInfoFailure),
                    take(1),
                    timeout(10000),
                  ),
                )
                  .then(() => firstValueFrom(store.select(userInfo.selectUserInfo)))
                  .catch(() => null);
              }

              if (userInfoState?.permissions) {
                // Map API format to tracking format
                const trackingData = {
                  country: userInfoState.permissions.country?.id || null,
                  business_partner_type: userInfoState.permissions.roles?.[0]?.id || null,
                  is_internal_user: 'false',
                };
                //console.log('[Tracking] Extracted tracking data from UserInfo:', trackingData);

                if (!trackingData.country || !trackingData.business_partner_type) {
                  console.warn(`[Tracking] ${clientData.name} - Missing tracking data fields`);
                }

                return trackingData;
              } else {
                console.warn(`[Tracking] ${clientData.name} - UserInfo not available in store`);
              }
            }
          }
        } catch (error) {
          console.error('[Tracking] Error getting token for tracking:', error);
        }

        //console.log('[Tracking] No token available for tracking');
        return null;
      },
      extractionConfig: {
        countryTrackingPropertyKey: 1, // Mapp category property key for country
        roleTrackingPropertyKey: 2, // Mapp category property key for role
        isInternalUserTrackingPropertyKey: 3, // Optional: Mapp category property key for internal user flag
      },
    }),
    {
      provide: REDIRECT_CONFIG,
      useValue: {
        api: {
          url: environment.apiData.apiURL,
        },
        targetHost: environment.redirect.targetHost,
      },
    },
    MaintenanceService,
    FeatureFlagsService,
    TogglesFacade,
    {
      provide: UNLEASH_CONFIGURATION,
      useValue: environment.unleash,
    },
    {
      provide: ErrorHandler,
      useClass: ChunkLoadAwareErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
